import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  PropsWithChildren,
} from "react";
import type { RecipientContact } from "../Utils/contacts";

/* ------------------------------------------------------------------
   Types
-------------------------------------------------------------------*/
export type RecipientRole = "Signer" | "Viewer";

export interface Recipient {
  id: string;
  name: string;
  email: string;
  role: RecipientRole;
  order?: number; // optional signing order
  /** Other emails / mobile numbers for the SAME person */
  contacts?: RecipientContact[];
}

/**
 * In-progress envelope data across the 4-step send flow.
 * Exactly one "start doc" mode at a time:
 *   - file (uploaded PDF/DOCX)
 *   - templateId (server template; content hydrated into QuickDoc)
 *   - isBlank=true (QuickDoc free-form path)
 */
export interface DraftEnvelope {
  /* START DOC SELECTION */
  file?: File;
  templateId?: string;
  isBlank?: boolean;

  /* QuickDoc / in-page HTML */
  quickDocHtml?: string;
  quickDocText?: string;

  /* Optional metadata */
  title?: string;
  description?: string;
  tags?: string[];

  /* Custom message for recipients */
  message?: string;

  /* Recipients */
  recipients: Recipient[];
}

const emptyDraft: DraftEnvelope = {
  recipients: [],
  message: "",
};

/* ------------------------------------------------------------------
   Context shape
-------------------------------------------------------------------*/
interface SendFlowValue {
  draft: DraftEnvelope;
  setDraft: React.Dispatch<React.SetStateAction<DraftEnvelope>>;

  /* start doc helpers */
  setStartDocFile: (file: File) => void;
  setStartDocTemplate: (templateId: string, templateHtml?: string) => void;
  /** QuickDoc path, optionally hydrate with html */
  setStartDocQuick: (html?: string) => void;

  /* recipient helpers */
  addRecipient: (r: Omit<Recipient, "id"> & { id?: string }) => string;
  updateRecipient: (id: string, patch: Partial<Omit<Recipient, "id">>) => void;
  removeRecipient: (id: string) => void;
  replaceRecipients: (recs: Recipient[]) => void;

  /* custom message for recipients */
  message: string;
  setMessage: (msg: string) => void;

  resetDraft: () => void;
  hasStartDoc: boolean;
}

const SendFlowCtx = createContext<SendFlowValue | undefined>(undefined);

export const useSendFlow = (): SendFlowValue => {
  const ctx = useContext(SendFlowCtx);
  if (!ctx) {
    throw new Error("useSendFlow must be used within <SendFlowProvider>.");
  }
  return ctx;
};

/* ------------------------------------------------------------------
   Provider
-------------------------------------------------------------------*/
export const SendFlowProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [draft, setDraft] = useState<DraftEnvelope>(emptyDraft);
  const [message, setMessage] = useState("");

  /* ---- start doc: upload ---- */
  const setStartDocFile = useCallback((file: File) => {
    setDraft((d) => ({
      ...d,
      file,
      templateId: undefined,
      isBlank: false,
      quickDocHtml: undefined,
      quickDocText: undefined,
    }));
  }, []);

  /* ---- start doc: template ---- */
  const setStartDocTemplate = useCallback(
    (templateId: string, templateHtml?: string) => {
      setDraft((d) => ({
        ...d,
        templateId,
        file: undefined,
        // We still let user edit a *copy* in QuickDoc.
        // If you want locked template, set isBlank=false. For now we treat as QuickDoc start.
        isBlank: true,
        quickDocHtml: templateHtml ?? d.quickDocHtml ?? "",
        quickDocText: templateHtml
          ? stripHtml(templateHtml)
          : d.quickDocText ?? "",
      }));
    },
    []
  );

  /* ---- start doc: QuickDoc blank or with provided html ---- */
  const setStartDocQuick = useCallback((html?: string) => {
    setDraft((d) => ({
      ...d,
      isBlank: true,
      file: undefined,
      templateId: undefined,
      quickDocHtml: html ?? d.quickDocHtml ?? "",
      quickDocText: stripHtml(html ?? d.quickDocHtml ?? ""),
    }));
  }, []);

  /* ----- recipient helpers ----- */
  const genId = () =>
    (window.crypto?.randomUUID?.() ??
      `rec_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 8)}`) as string;

  const addRecipient = useCallback(
    (r: Omit<Recipient, "id"> & { id?: string }) => {
      const id = r.id ?? genId();
      const newRec: Recipient = {
        id,
        name: r.name,
        email: r.email,
        role: r.role,
        order: r.order,
        ...(r.contacts?.length ? { contacts: r.contacts } : {}),
      };
      setDraft((d) => ({
        ...d,
        recipients: [...d.recipients, newRec],
      }));
      return id;
    },
    []
  );

  const updateRecipient = useCallback(
    (id: string, patch: Partial<Omit<Recipient, "id">>) => {
      setDraft((d) => ({
        ...d,
        recipients: d.recipients.map((rec) =>
          rec.id === id ? { ...rec, ...patch } : rec
        ),
      }));
    },
    []
  );

  const removeRecipient = useCallback((id: string) => {
    setDraft((d) => ({
      ...d,
      recipients: d.recipients.filter((rec) => rec.id !== id),
    }));
  }, []);

  const replaceRecipients = useCallback((recs: Recipient[]) => {
    setDraft((d) => ({
      ...d,
      recipients: recs,
    }));
  }, []);

  const resetDraft = useCallback(() => {
    setDraft(emptyDraft);
    setMessage("");
  }, []);

  const hasStartDoc = Boolean(draft.file || draft.templateId || draft.isBlank);

  const value: SendFlowValue = {
    draft,
    setDraft,
    setStartDocFile,
    setStartDocTemplate,
    setStartDocQuick,
    addRecipient,
    updateRecipient,
    removeRecipient,
    replaceRecipients,
    message,
    setMessage,
    resetDraft,
    hasStartDoc,
  };

  return <SendFlowCtx.Provider value={value}>{children}</SendFlowCtx.Provider>;
};

/* local helper */
function stripHtml(html: string): string {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}
